import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subject, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Localises every <mat-paginator> in the app.
 *
 * The default MatPaginatorIntl is English-only and builds its range label as
 * `"{start} – {end} of {length}"`. Rendered inside an RTL paragraph the bidi
 * algorithm reorders those runs, which is why Hebrew screens showed
 * "of 6 6 – 1" instead of "1–6 מתוך 6".
 *
 * Two things are needed, and neither can be done per-screen:
 *  1. the sentence must be one translatable unit so translators control word
 *     order (see the i18n rule in CLAUDE_CODE_BRIEF_MASTER.md §19);
 *  2. the numeric runs must be bidi-isolated. getRangeLabel returns a plain
 *     string, so `<bdi>` is unavailable — the Unicode isolate controls below
 *     are the text-level equivalent.
 */
const LRI = '⁦'; // LEFT-TO-RIGHT ISOLATE
const PDI = '⁩'; // POP DIRECTIONAL ISOLATE
const EN_DASH = '–';

/** Pins a numeric run to LTR so surrounding RTL text cannot reorder it. */
const isolate = (value: string | number): string => `${LRI}${value}${PDI}`;

@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    super();

    // onTranslationChange also fires when the initial language file resolves,
    // which can land after construction — without it the static labels would
    // stay stuck on their raw keys until the user switched language.
    merge(this.translate.onLangChange, this.translate.onTranslationChange)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyLabels());

    this.applyLabels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyLabels(): void {
    this.itemsPerPageLabel = this.translate.instant('common.paginator.itemsPerPage');
    this.nextPageLabel = this.translate.instant('common.paginator.nextPage');
    this.previousPageLabel = this.translate.instant('common.paginator.previousPage');
    this.firstPageLabel = this.translate.instant('common.paginator.firstPage');
    this.lastPageLabel = this.translate.instant('common.paginator.lastPage');
    this.changes.next();
  }

  // Declared as a property on the base class, so it must be assigned as one.
  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const total = Math.max(length, 0);

    if (total === 0 || pageSize === 0) {
      return this.translate.instant('common.paginator.rangeEmpty', { total: isolate(total) });
    }

    const start = page * pageSize;
    const end = Math.min(start + pageSize, total);

    return this.translate.instant('common.paginator.range', {
      range: isolate(`${start + 1}${EN_DASH}${end}`),
      total: isolate(total)
    });
  };
}
